import {
  Component,
  Input,
  Output,
  EventEmitter,
  signal,
  computed,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule, Filter } from 'lucide-angular';
import { DrawerModule } from 'primeng/drawer';
import type { Producto, Atributo } from '../../../types/product.types';
import { extractQuickFilters } from '../../../utils/products/quick-filter.utils';

export interface QuickFilter {
  label: string;
  value: string;
  count: number;
  selected: boolean;
}

export interface FilterOption {
  value: string;
  label: string;
  count: number;
  selected: boolean;
}

export interface FilterCategory {
  name: string;
  key: string;
  options: FilterOption[];
  expanded: boolean;
  showAll: boolean;
  searchTerm: string;
}

@Component({
  selector: 'allas-quick-filters-touchbar',

  imports: [CommonModule, FormsModule, LucideAngularModule, DrawerModule],
  template: `
    <div
      class="flex items-center gap-2 overflow-x-auto relative scroll-hidden w-full px-4"
    >
      <span class="flex-shrink-0 xl:hidden" (click)="openFiltersDrawer.emit()">
        <button
          class="flex items-center gap-2 px-3 py-2 bg-gray-100 border border-gray-200 rounded-lg text-gray-700 text-xs font-medium hover:bg-gray-200 transition-colors"
        >
          <lucide-icon [name]="filterIcon" class="w-4 h-4"></lucide-icon>
        </button>
      </span>
      @for (filter of availableQuickFilters(); track filter.value) {
      <span class="flex-shrink-0 fs-condensed" (click)="onFilterClick(filter)">
        <button
          class="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg text-xs font-medium transition-colors fs-condensed"
          [class.bg-appPrimary-500]="filter.selected"
          [class.text-white]="filter.selected"
          [class.border-appPrimary-500]="filter.selected"
          [class.bg-gray-100]="!filter.selected"
          [class.text-gray-700]="!filter.selected"
        >
          {{ filter.label }}
          @if (filter.count > 0) {
          <span class="ml-1 text-xs opacity-75">({{ filter.count }})</span>
          }
        </button>
      </span>
      }
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
})
export class QuickFiltersTouchbar {
  @Input() products: Producto[] = [];
  @Input() selectedQuickFilter: string | null = null;
  internalSelectedQuickFilter = signal<string | null>(null);

  @Output() filtersChanged = new EventEmitter<Record<string, string[]>>();
  @Output() openFiltersDrawer = new EventEmitter<void>();
  @Output() onQuickFilterSelected = new EventEmitter<string | null>();

  readonly filterIcon = Filter;

  // Computed que extrae los filtros rápidos disponibles de los productos
  availableQuickFilters = computed<QuickFilter[]>(() => {
    if (!this.products || this.products.length === 0) {
      return [];
    }

    const extractedFilters = extractQuickFilters(this.products);
    const currentSelected = this.internalSelectedQuickFilter();

    // Convertir a formato QuickFilter y marcar como seleccionado si corresponde
    return extractedFilters
      .slice(0, 10) // Limitar a 10 filtros más populares
      .map((filter) => ({
        label: filter.label,
        value: filter.filterKey,
        count: filter.count,
        selected: currentSelected === filter.filterKey,
      }));
  });

  onFilterClick(filter: QuickFilter): void {
    if (filter.selected) {
      // Si ya está seleccionado, deseleccionarlo
      this.internalSelectedQuickFilter.set(null);
      this.onQuickFilterSelected.emit(null);
    } else {
      // Seleccionar el nuevo filtro
      this.internalSelectedQuickFilter.set(filter.value);
      this.onQuickFilterSelected.emit(filter.value);
    }
  }
}
